import React, { FunctionComponent } from 'react';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import moment from 'moment';

import { ICourseTemplate } from '../../../../../../../store/V2/CourseTemplatesSlice';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../../components/V2/Table/type1/TableStyles';
import { IUser } from '../../../../../../../store/UserSlice';
import { ICourse } from '../../../../../../../store/V2/CoursesSlice';
import { checkUserPermission } from '../../../../../../../helper';
import { useAppSelector } from '../../../../../../../hooks';
import { getAuthState } from '../../../../../../../store/AuthSlice';
import { USER_PERMISSION_INDIVIDUALS } from '../../../../../../../store/V2/RolesSlice';
import { getTenantsState } from '../../../../../../../store/V2/TenantsSlice';

interface CourseTemplateTableRowProps {
  courseTemplate: ICourseTemplate;
  isActionsDisabled: boolean;
  onUpdate: () => void;
  onDelete: () => void;
}

const CourseTemplateTableRow: FunctionComponent<CourseTemplateTableRowProps> = ({
  courseTemplate,
  isActionsDisabled = false,
  onUpdate,
  onDelete,
}) => {
  // Store
  const authState = useAppSelector(getAuthState);
  const tenantState = useAppSelector(getTenantsState);

  const { user: authUser } = authState;
  const { appTenant } = tenantState;

  const createdAtMoment = moment(courseTemplate.createdAt);
  const createdAtDate = createdAtMoment.format('ddd MMM DD YYYY');
  const createdAtTime = createdAtMoment.format('hh:mm A');
  const createdByData = courseTemplate.createdBy as IUser;
  const updatedByData = courseTemplate.lastUpdatedBy as IUser;
  const courseData = courseTemplate.course as ICourse;

  const courseTitle =
    courseData && typeof courseData === 'object' ? courseData.title : '';

  const createdByName =
    createdByData && typeof createdByData === 'object'
      ? `${createdByData.firstname} ${createdByData.lastname || ''}`.trim()
      : '';

  const lastUpdatedByName =
    updatedByData && typeof updatedByData === 'object'
      ? `${updatedByData.firstname} ${updatedByData.lastname || ''}`.trim()
      : '';

  const updatePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminCourseTemplate: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_EDIT,
      ],
    },
  });

  const deletePermission = checkUserPermission({
    authUser,
    tenant: appTenant,
    permissions: {
      adminCourseTemplate: [
        USER_PERMISSION_INDIVIDUALS.ADMIN_COURSE_TEMPLATE_DELETE,
      ],
    },
  });

  return (
    <StyledTableRow hover key={courseTemplate._id}>
      <StyledTableCell>
        <Stack>
          <Typography fontSize="14px" fontWeight={500} color="#1E293B">
            {createdAtDate}
          </Typography>
          <Typography fontSize="12px" color="#94A3B8">
            {createdAtTime}
          </Typography>
        </Stack>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTemplate.name || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTitle || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTemplate.curriculum || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTemplate.subject || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTemplate.grade || '-'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>
        <Typography
          fontSize="14px"
          fontWeight={500}
          color="#1E293B"
          className="ellipsis-text two-lines"
        >
          {courseTemplate.isTrialTemplate ? 'Yes' : 'No'}
        </Typography>
      </StyledTableCell>
      <StyledTableCell>{createdByName || ''}</StyledTableCell>
      <StyledTableCell>{lastUpdatedByName || ''}</StyledTableCell>
      <StyledTableCell>
        {updatePermission ? (
          <Tooltip title="Edit">
            <IconButton onClick={onUpdate} disabled={isActionsDisabled}>
              <EditIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
        ) : null}

        {deletePermission ? (
          <Tooltip title="Edit">
            <IconButton onClick={onDelete} disabled={isActionsDisabled}>
              <DeleteIcon sx={{ color: '#1E293B' }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default CourseTemplateTableRow;
